import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import api from '../api/api';
import { clearDiscountRules } from '../utils/discountRules';

const AuthContext = createContext(null);

const LOGO_CACHE_KEY = 'shopLogoDataUrl';
// Same key as DUE_POPUP_SEEN_KEY in SubscriptionDueNotice.js
const DUE_POPUP_SEEN_KEY = 'subscriptionDuePopupSeen';

function clearDuePopupSeen() {
  try {
    localStorage.removeItem(DUE_POPUP_SEEN_KEY);
  } catch (err) {
    // Storage blocked: nothing to clear.
  }
}

// expires: 1 matches the backend's real jwt.expiration (24h). secure is only forced on
// the production build — forcing it unconditionally would stop the cookie from being set
// during local `npm start` over plain http, silently breaking local dev login. sameSite:
// 'strict' is safe here because this cookie is never sent by the browser as a real
// Cookie header — it's only read via Cookies.get and placed manually in the Authorization
// header by api.js.
const authCookieOptions = {
  expires: 1,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // logoDataUrl holds the base64 data URL of the shop logo, cached for the session.
  // null = not loaded yet, '' = no logo, 'data:...' = loaded image.
  const [logoDataUrl, setLogoDataUrl] = useState(() => {
    return sessionStorage.getItem(LOGO_CACHE_KEY) || null;
  });
  // Track if a fetch is in-flight so we never issue two requests concurrently.
  const logoFetchingRef = useRef(false);

  useEffect(() => {
    const token = Cookies.get('authToken');
    const stored = Cookies.get('userInfo');
    if (token && stored) {
      try { setUser(JSON.parse(stored)); } catch { }
    }
    setLoading(false);
  }, []);

  // Fetch the logo image exactly once per session and cache as base64.
  // Called automatically when the logoUrl in userInfo becomes available.
  //
  // Routed through our own /user/profileLogo endpoint instead of fetch()-ing the R2 URL
  // (hasLogo here is really just "does the user have one uploaded?", not the URL itself —
  // the backend looks up the current user's own logo from their JWT, no param needed).
  // R2's public r2.dev domain doesn't send Access-Control-Allow-Origin, so a direct
  // cross-origin fetch() was CORS-blocked — the request succeeded (200, visible in
  // devtools) but the browser refused to let JS read the response body, so this silently
  // resolved to '' via the catch below and the image never rendered. Our own API already
  // works for every other cross-origin call the app makes, so proxying through it sidesteps
  // R2's CORS config entirely.
  const fetchLogoOnce = useCallback((hasLogo) => {
    if (!hasLogo) { setLogoDataUrl(''); return; }
    // Already cached in sessionStorage for this session — use it directly.
    const cached = sessionStorage.getItem(LOGO_CACHE_KEY);
    if (cached) { setLogoDataUrl(cached); return; }
    if (logoFetchingRef.current) return;
    logoFetchingRef.current = true;
    api.get('/user/profileLogo', { responseType: 'blob' })
      .then(r => new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(r.data);
      }))
      .then(dataUrl => {
        sessionStorage.setItem(LOGO_CACHE_KEY, dataUrl);
        setLogoDataUrl(dataUrl);
      })
      .catch(() => { setLogoDataUrl(''); })
      .finally(() => { logoFetchingRef.current = false; });
  }, []);

  // Update the cached logo data URL when it changes (e.g. after upload).
  const setLogoCache = useCallback((dataUrl) => {
    if (dataUrl) {
      sessionStorage.setItem(LOGO_CACHE_KEY, dataUrl);
    } else {
      sessionStorage.removeItem(LOGO_CACHE_KEY);
    }
    setLogoDataUrl(dataUrl || '');
  }, []);

  const login = (token, userInfo) => {
    Cookies.set('authToken', token, authCookieOptions);
    Cookies.set('userInfo', JSON.stringify(userInfo), authCookieOptions);
    setUser(userInfo);
    // Clear any previous session's logo cache on new login.
    sessionStorage.removeItem(LOGO_CACHE_KEY);
    // Show the payment due popup once again for this login.
    clearDuePopupSeen();
    clearDiscountRules();
    setLogoDataUrl(null);
  };

  const logout = () => {
    Cookies.remove('authToken');
    Cookies.remove('userInfo');
    Cookies.remove('enableInventoryLowStockAlert');
    sessionStorage.removeItem(LOGO_CACHE_KEY);
    clearDuePopupSeen();
    clearDiscountRules();
    setUser(null);
    setLogoDataUrl(null);
  };

  const updateUser = useCallback((updated) => {
    setUser(prev => {
      const merged = { ...prev, ...updated };
      Cookies.set('userInfo', JSON.stringify(merged), authCookieOptions);
      return merged;
    });
  }, []); // stable — uses functional setUser, no closure over `user`

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading, logoDataUrl, fetchLogoOnce, setLogoCache }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
